import { renderColorByString } from 'components/DataGrid/renderColorByString'
import dayjs from 'dayjs'
import { heading } from './description_format'
import { Channel, DescKey, Platform, Row, SubOption, TaskTypeDefinition, TaskTypeId } from './taskTypes'

export function makeDefaultDescription(
  id: TaskTypeId,
  ctx: Parameters<NonNullable<TaskTypeDefinition['makeDescription']>>[0]
): string | null {
  const def = TaskTypeRegistry.find(d => d.id === id)
  return def?.makeDescription ? def.makeDescription(ctx) : null
}

const simpleDesc = (title: string, extra?: string[]) =>
  `
<p><strong>${title}</strong></p>
${extra?.length ? `<ul>${extra.map(li => `<li>${li}</li>`).join('')}</ul>` : ''}
`.trim()
const labelWithSub = (base: string, sub?: string) => (sub ? `${base} – ${sub}` : base)

const BUDGET_SUBS: SubOption[] = ['Increase', 'Decrease', 'Recommendation', 'Audit']

const INTEGRATION_SUBS: SubOption[] = ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking']

const APPROVALS_SUBS: SubOption[] = ['Search Ads', 'Social Ads', 'Display Ads', 'Video Ads']

const PAUSE_ENABLE_SUBS: SubOption[] = ['All Campaigns', 'Single Campaign', 'Ad Group / Ad Set', 'Creative']

const NCO_SUBS: SubOption[] = ['Quality Assurance', 'Kickoff Prep', 'Other']

const CAMPAIGN_CHECKLIST_LIBRARY: Record<string, string[]> = {
  // keys are sub-options; feel free to split per platform/channel if you want
  'Search – New Build': [
    'Confirm goals & KPIs',
    'Keyword research',
    'Account structure',
    'Ads & extensions',
    'Bidding & budgets',
    'Tracking & QA',
  ],
  'Search – Monthly Optimization': [
    'Search terms review',
    'Bids/pacing adjustments',
    'Ad tests review',
    'Negative keywords',
    'Annotate changes',
  ],
  'Social – Prospecting': [
    'Audience selection',
    'Creative mapping',
    'Placements & exclusions',
    'Conversion event set',
    'QA & launch',
  ],
  'Social – Retargeting': [
    'Engagement/source audiences',
    'Creative sizes ready',
    'Frequency/pacing check',
    'UTMs & pixels live',
    'QA & launch',
  ],
  'Display – Standard': [
    'Audience / site lists',
    'Creative set ready',
    'Brand safety',
    'Pacing & caps',
    'Conversions tracking',
  ],
  'Video – Awareness': [
    'Targeting selection',
    'Video creative checks',
    'Bumper/Skippable mix',
    'Brand safety',
    'Conversion tracking',
  ],
}
const COMMON_UPDATE_SUBS: SubOption[] = [
  'Performance',
  'Ad Copy',
  'Website',
  'Keywords',
  'Search Terms',
  'Audience',
  'Targeting',
  'Campaign Settings',
  'Placements',
  'Other',
]

const COMMON_REVIEW_SUBS: SubOption[] = [
  'Website',
  'Keywords / Keyword Rankings',
  'Google Business Profile',
  'NAP / Hours',
]

const CREATE_SETUP_SUBS: SubOption[] = [
  'Performance',
  'Ad Copy',
  'Website',
  'Keywords',
  'Search Terms',
  'Campaign Settings',
  'Other',
]

// Helpers
const titled = (s?: string | null) => (s ?? '').trim()
const dash = (a: string, b?: string | null) =>
  b ? `<span style="color: ${renderColorByString(a)}">${a}</span> > ${b}` : a

const renderDesc = (title: string, bullets: string[]) =>
  `
<p><strong style="color: rgb(107, 185, 240)">${title}</strong></p>
<ul>${bullets.map(li => `<li>${li}</li>`).join('')}</ul>
`.trim()

export const TaskTypeRegistry: TaskTypeDefinition[] = [
  {
    id: 'updates',
    label: 'Updates',
    allowed: [{}],
    subOptions: COMMON_UPDATE_SUBS,
    makeDescription: ({ channel, platform, subOption }) =>
      renderDesc(heading(platform, channel, 'Updates', titled(subOption)), []),
  },

  {
    id: 'budget',
    label: 'Budget',
    allowed: [{}],
    subOptions: BUDGET_SUBS,
    makeDescription: ({ channel, platform, subOption }) => {
      const sub = titled(subOption) || 'Adjustment'

      const BULLETS_BY_SUB: Record<string, string[]> = {
        Increase: [
          'Calculate delta vs plan and forecast impact',
          'Raise caps / daily budgets and confirm pacing',
          'Annotate change rationale in platform/reporting',
        ],
        Decrease: [
          'Identify underspend / performance drivers',
          'Lower caps / daily budgets and confirm pacing',
          'Document rationale and next review checkpoint',
        ],
        Recommendation: [
          'Assess performance vs goals and seasonality',
          'Propose new daily/monthly caps with rationale',
          'Outline risks/assumptions and request approval',
        ],
        Audit: [
          'Check budget caps, shared budgets, and limits',
          'Verify pacing rules and delivery method',
          'Confirm annotations and historical changes',
        ],
      }

      const bullets = BULLETS_BY_SUB[sub] ?? [
        'Calculate delta vs plan',
        'Update budget caps & pacing rules',
        'Confirm in platform and annotate report',
      ]

      // e.g. "Website | Budget > Increase" or "Budget > Increase" if no platform/channel
      return renderDesc(heading(platform, channel, 'Budget', sub), []) // or use `bullets` if you want them
    },
  },

  {
    id: 'createSetup',
    label: 'Create Setup',
    allowed: [{}],
    subOptions: CREATE_SETUP_SUBS,
    makeDescription: ({ channel, platform, subOption }) =>
      simpleDesc(heading(platform, channel, 'Setup', subOption), []),
  },

  {
    id: 'review',
    label: 'Review',
    allowed: [{}],
    subOptions: COMMON_REVIEW_SUBS,
    makeDescription: ({ channel, platform, subOption }) =>
      renderDesc(heading(platform, channel, 'Review', titled(subOption)), []),
  },
  {
    id: 'campaignChecklists',
    label: 'Campaign Checklists',
    allowed: [{}],
    subOptions: ctx => {
      // make the sub-options feel platform/channel-aware
      const out: SubOption[] = []
      if (ctx.channel === 'Search' || ctx.channel === 'Digital') {
        out.push('Search – New Build', 'Search – Monthly Optimization')
      }
      if (ctx.channel === 'Social' || ctx.platform === 'Meta' || ctx.platform === 'LinkedIn') {
        out.push('Social – Prospecting', 'Social – Retargeting')
      }
      if (ctx.channel === 'Display') {
        out.push('Display – Standard')
      }
      if (ctx.channel === 'Video' || ctx.platform === 'YouTube') {
        out.push('Video – Awareness')
      }
      return out.length ? out : ['General – Campaign Checklist']
    },
    checklistBySub: CAMPAIGN_CHECKLIST_LIBRARY,
    makeDescription: ({ subOption }) => simpleDesc(subOption || 'Campaign Checklist'),
  },
  {
    id: 'adApprovals',
    label: 'Ad Approvals',
    allowed: [{}],
    subOptions: APPROVALS_SUBS,
    makeDescription: ({ channel, platform, subOption }) =>
      // simpleDesc(`Approval - ${subOption || 'Ads'}`, ['Preview assets', 'Collect feedback', 'Record final decision']),
      renderDesc(heading(platform, channel, 'Approval', titled(subOption)), []),
  },
  {
    id: 'integration',
    label: 'Integration',
    allowed: [{}],
    subOptions: INTEGRATION_SUBS,
    makeDescription: ({ channel, platform, subOption }) =>
      // renderDesc(dash('Integration', titled(subOption)), [
      //   'Implement/verify tags',
      //   'QA in realtime tools',
      //   'Document events/params',
      // ]),
      renderDesc(heading(platform, channel, 'Integration', titled(subOption)), []),
  },
  {
    id: 'creativeProject',
    label: 'Creative Project',
    allowed: [{}],
    subOptions: [],
    makeDescription: ({ channel, platform, subOption }) =>
      // renderDesc(dash('Creative Project', titled(subOption)), [
      //   // 'Implement/verify tags',
      //   // 'QA in realtime tools',
      //   // 'Document events/params',
      // ]),
      renderDesc(heading(platform, channel, 'Creative Project', titled(subOption)), []),
  },
]
const AUTO_DESC_MARK = 'AUTO_DESC' // marker so we can replace it idempotently

export const DEFAULT_DESCRIPTIONS: Array<{
  match: (k: DescKey) => boolean
  make: (ctx: {
    orgName?: string
    platform?: string
    channel?: string
    taskType: string
    taskTypeLabel?: string
    productTitle?: string
    start?: string | null
    due?: string | null
  }) => string // returns HTML
}> = [
  // --- Integration family ---
  {
    match: k => k.taskType.toLowerCase() === 'integration' && /ga4/i.test(k.taskTypeLabel || ''),
    make: ({ orgName, productTitle, start, due, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:integration-ga4-->
<p><strong>${labelWithSub('Integration', rest?.subType || 'GA4')}</strong> for ${orgName ?? 'organization'}${
        productTitle ? ` (${productTitle})` : ''
      }.</p>
<ul>
  <li>Create data streams and set measurement ID</li>
  <li>Configure recommended events & conversions</li>
  <li>Link to Google Ads (if applicable)</li>
  <li>Verify real-time collection and debug view</li>
</ul>
<p><em>Window:</em> ${start ?? 'TBD'} → ${due ?? 'TBD'}</p>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },
  {
    match: k => k.taskType.toLowerCase() === 'integration' && /gtm|tag manager/i.test(k.taskTypeLabel || ''),
    make: ({ orgName, productTitle, start, due, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:integration-gtm-->
<p><strong>${labelWithSub('Integration', rest?.subType || 'GTM')}</strong> for ${orgName ?? 'organization'}${
        productTitle ? ` (${productTitle})` : ''
      }.</p>
<ul>
  <li>Workspace & environments</li>
  <li>Base container snippet install & consent checks</li>
  <li>Core tags/triggers/variables</li>
  <li>Preview/Debug & publish</li>
</ul>
<p><em>Window:</em> ${start ?? 'TBD'} → ${due ?? 'TBD'}</p>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },
  {
    match: k => k.taskType.toLowerCase() === 'integration' && /conversion|event/i.test(k.taskTypeLabel || ''),
    make: ({ orgName, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:integration-conv-->
<p><strong>${labelWithSub('Integration', rest?.subType || 'Conversion Tracking')}</strong> baseline for ${
        orgName ?? 'organization'
      }.</p>
<ul>
  <li>Define primary conversion events</li>
  <li>Implement tags (GTM/GA4/Pixel as applicable)</li>
  <li>QA in real-time & debug tools</li>
  <li>Document event names & parameters</li>
</ul>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },
  // --- Updates ---
  {
    match: k => k.taskType.toLowerCase() === 'updates',
    make: ({ channel, taskTypeLabel, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:updates-->
<p><strong>${labelWithSub(channel || 'Channel', rest?.subType || 'Updates')}</strong></p>
<ul>
  <li>Review performance & anomalies</li>
  <li>Apply scoped adjustments (bids/budgets, creatives, keywords)</li>
  <li>Record changes & rationale</li>
</ul>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },

  // --- Budget ---
  {
    match: k => k.taskType.toLowerCase() === 'budget',
    make: ({ channel, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:budget-->
<p><strong>${labelWithSub(channel || 'Channel', rest?.subType || 'Budget Adjustment')}</strong></p>
<ul>
  <li>Calculate delta vs plan</li>
  <li>Update budget caps & pacing rules</li>
  <li>Confirm in platform and annotate report</li>
</ul>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },

  // --- Review (Website / Keywords / GBP etc.) ---
  {
    match: k => k.taskType.toLowerCase() === 'review' && /website/i.test(k.taskTypeLabel || ''),
    make: () =>
      `
<!--${AUTO_DESC_MARK}:review-website-->
<p><strong>Website Review</strong></p>
<ul>
  <li>Check speed, mobile usability, and key landing pages</li>
  <li>Confirm tracking snippets present & firing</li>
  <li>Identify top 3 quick wins</li>
</ul>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },
  // --- Creative Projects ---
  {
    match: k => k.taskType.toLowerCase() === 'creativeProjects' && /ga4/i.test(k.taskTypeLabel || ''),
    make: ({ orgName, productTitle, start, due, ...rest }: any) =>
      `
<!--${AUTO_DESC_MARK}:creative-ga4-->
<p><strong>${labelWithSub('Creative Projects', rest?.subType || 'GA4')}</strong> for ${orgName ?? 'organization'}${
        productTitle ? ` (${productTitle})` : ''
      }.</p>
<ul>
  <li>Create data streams and set measurement ID</li>
  <li>Configure recommended events & conversions</li>
  <li>Link to Google Ads (if applicable)</li>
  <li>Verify real-time collection and debug view</li>
</ul>
<p><em>Window:</em> ${start ?? 'TBD'} → ${due ?? 'TBD'}</p>
<!--/${AUTO_DESC_MARK}-->
`.trim(),
  },
]

export const makeAutoDescSnippet = (row: any): { html: string | null; keyId: string | null } => {
  console.log('row', row)
  const key: DescKey = {
    platform: row.platform,
    channel: row.channel,
    taskType: row.taskType,
    taskTypeLabel: row.taskTypeLabel,
    subType: row.subType,
  }
  const match = DEFAULT_DESCRIPTIONS.find(d => d.match(key))
  if (!match) return { html: null, keyId: null }

  const start = row?.startDate ? dayjs(row.startDate).format('YYYY-MM-DD') : null
  const due = row?.endDate
    ? dayjs(row.endDate).format('YYYY-MM-DD')
    : row?.dateDue
    ? dayjs(row.dateDue).format('YYYY-MM-DD')
    : null

  const html = match.make({
    orgName: row?.org?.name,
    platform: row.platform,
    channel: row.channel,
    taskType: row.taskType,
    taskTypeLabel: row.taskTypeLabel,
    productTitle: row?.selectedProduct?.title,
    start,
    due,
  })

  // derive the marker id that was used inside <!--AUTO_DESC:...-->
  const keyIdMatch = html.match(new RegExp(`<!--${AUTO_DESC_MARK}:([^>]+)-->`))
  const keyId = keyIdMatch?.[1] ?? null
  return { html, keyId }
}

/** remove any previous AUTO_DESC block (regardless of which flavor) */
export const stripAutoDesc = (html: string = ''): string =>
  html.replace(new RegExp(`<!--${AUTO_DESC_MARK}:[\\s\\S]*?-->[\\s\\S]*?<!--\\/${AUTO_DESC_MARK}-->`, 'g'), '').trim()

/** insert or replace the AUTO_DESC block, keeping user's freeform content */
export const upsertAutoDesc = (existingHtml: string = '', newBlockHtml: string): string => {
  const rest = stripAutoDesc(existingHtml)
  if (!rest) return newBlockHtml // if empty, just drop the block
  // put our auto section on top, keep the user's content below
  return `${newBlockHtml}\n<hr/>\n${rest}`
}

// buildAutoDescription.ts
export const buildAutoDescription = (row: Row): string => {
  const base = '' // keep your current behavior

  if (!row.taskType) return stripAutoDesc(base)

  const v2 = makeDefaultDescription(row.taskType as any, {
    platform: row.platform as Platform,
    channel: row.channel as Channel,
    subOption: row.subType || row.taskTypeLabel || undefined, // <— prefer subType
    orgName: row?.org?.name,
    productTitle: row?.selectedProduct?.title,
    start: row?.startDate ? dayjs(row.startDate).format('YYYY-MM-DD') : null,
    due: row?.dateDue ? dayjs(row.dateDue).format('YYYY-MM-DD') : null,
  })

  if (v2) return upsertAutoDesc(base, v2)

  // fallback templates:
  const { html } = makeAutoDescSnippet(row)
  return html ? upsertAutoDesc(base, html) : stripAutoDesc(base)
}
